import { useCallback, useMemo, useState, Dispatch, SetStateAction } from 'react';
import type { AttentionItem, JiraIssue } from '../../domain/types';
import { attentionStatusForWorkflow } from '../../domain/mappers';
import { jiraClient } from '../../services/clients';
import { createIdempotencyKey } from '../../services/idempotencyKey';

export interface JiraSliceOptions {
  setItems: Dispatch<SetStateAction<AttentionItem[]>>;
  beginWrite: () => void;
  retry: (source?: string) => Promise<void>;
  setErrors?: Dispatch<SetStateAction<Record<string, string | null>>>;
}

/**
 * Apply an optimistic per-ticket update to the context's items array.
 *
 * Returns whether a matching item was actually found. The context's `items`
 * array is hydrated asynchronously (bootstrap fetch); a mutation triggered
 * before that fetch settles would otherwise silently no-op here and then be
 * permanently lost once the bootstrap fetch's own (unrelated) snapshot
 * replaces the jira slice of `items`. Callers use the return value to force
 * a resync in that case instead of dropping the mutation.
 */
function applyToTicket(
  setItems: Dispatch<SetStateAction<AttentionItem[]>>,
  ticketKey: string,
  updater: (item: AttentionItem) => AttentionItem,
): boolean {
  let found = false;
  setItems((prev) =>
    prev.map((item) => {
      if (item.ticket === ticketKey) {
        found = true;
        return updater(item);
      }
      return item;
    }),
  );
  return found;
}

export function useJiraSlice({ setItems, beginWrite, retry, setErrors }: JiraSliceOptions) {
  const [rawJiraIssues, setRawJiraIssues] = useState<JiraIssue[]>([]);

  const applyIssueMeta = useCallback(
    (ticketKey: string, issue: JiraIssue) => {
      const ai = issue.zuneloMeta?.ai?.trim();
      const next = issue.zuneloMeta?.next?.trim();
      setItems((prev) =>
        prev.map((item) =>
          item.ticket === ticketKey
            ? {
                ...item,
                ...(ai ? { ai } : {}),
                ...(next ? { next } : {}),
                rawJiraIssue: issue,
              }
            : item,
        ),
      );
      setRawJiraIssues((prev) => {
        const idx = prev.findIndex((i) => i.key === ticketKey);
        if (idx < 0) return [...prev, issue];
        const copy = [...prev];
        copy[idx] = issue;
        return copy;
      });
    },
    [setItems],
  );

  const updateWorkflowState = useCallback(
    async (ticketKey: string, newState: string) => {
      beginWrite();
      const nextStatus = attentionStatusForWorkflow(newState);

      const found = applyToTicket(setItems, ticketKey, (item) => ({
        ...item,
        workflow: newState,
        ...(nextStatus ? { st: nextStatus } : {}),
      }));

      try {
        const res = await jiraClient.updateWorkflowState(ticketKey, newState);
        applyIssueMeta(ticketKey, res.data);
        if (!found) await retry('jira');
      } catch {
        await retry('jira');
      }
    },
    [beginWrite, setItems, applyIssueMeta, retry],
  );

  const toggleAcceptanceCriterion = useCallback(
    async (ticketKey: string, index: number) => {
      beginWrite();
      const found = applyToTicket(setItems, ticketKey, (item) => {
        if (!item.criteria || !item.criteria[index]) return item;
        const updatedCriteria = [...item.criteria];
        const current = updatedCriteria[index];
        const currentState = current.status || current.state || 'open';
        const nextState = currentState === 'done' ? 'open' : 'done';
        updatedCriteria[index] = { ...current, status: nextState, state: nextState };
        return { ...item, criteria: updatedCriteria };
      });

      try {
        await jiraClient.toggleCriterion(ticketKey, index);
        if (!found) await retry('jira');
      } catch {
        await retry('jira');
      }
    },
    [beginWrite, setItems, retry],
  );

  const addAcceptanceCriterion = useCallback(
    async (ticketKey: string, text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      beginWrite();

      const found = applyToTicket(setItems, ticketKey, (item) => {
        const currentCriteria = item.criteria || [];
        return {
          ...item,
          criteria: [...currentCriteria, { status: 'open' as const, text: trimmed }],
        };
      });

      try {
        await jiraClient.addAcceptanceCriterion(ticketKey, trimmed);
        // The context's `items` array may not be hydrated with this ticket yet
        // (bootstrap fetch still in flight). The optimistic update above was a
        // no-op in that case, so force a resync now that the mutation has
        // committed server-side — otherwise the addition is silently lost once
        // the bootstrap fetch's stale snapshot lands.
        if (!found) await retry('jira');
      } catch {
        await retry('jira');
      }
    },
    [beginWrite, setItems, retry],
  );

  const updateAcceptanceCriterion = useCallback(
    async (ticketKey: string, index: number, text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      beginWrite();

      const found = applyToTicket(setItems, ticketKey, (item) => {
        if (!item.criteria || !item.criteria[index]) return item;
        const updatedCriteria = [...item.criteria];
        updatedCriteria[index] = { ...updatedCriteria[index], text: trimmed };
        return { ...item, criteria: updatedCriteria };
      });

      try {
        await jiraClient.updateAcceptanceCriterion(ticketKey, index, trimmed);
        if (!found) await retry('jira');
      } catch {
        await retry('jira');
      }
    },
    [beginWrite, setItems, retry],
  );

  const deleteAcceptanceCriterion = useCallback(
    async (ticketKey: string, index: number) => {
      beginWrite();
      const found = applyToTicket(setItems, ticketKey, (item) => {
        if (!item.criteria) return item;
        return {
          ...item,
          criteria: item.criteria.filter((_, i) => i !== index),
        };
      });

      try {
        await jiraClient.deleteAcceptanceCriterion(ticketKey, index);
        if (!found) await retry('jira');
      } catch {
        await retry('jira');
      }
    },
    [beginWrite, setItems, retry],
  );

  const cycleSubtaskState = useCallback(
    async (ticketKey: string, index: number) => {
      beginWrite();
      const found = applyToTicket(setItems, ticketKey, (item) => {
        if (!item.subtasks || !item.subtasks[index]) return item;
        const updatedSubtasks = [...item.subtasks];
        const current = updatedSubtasks[index];
        const cycle: Record<string, 'Hecho' | 'En curso' | 'Pendiente' | 'Bloqueado'> = {
          Pendiente: 'En curso',
          'En curso': 'Hecho',
          Hecho: 'Pendiente',
          Bloqueado: 'Pendiente',
        };
        const nextState = cycle[current.status] || 'Pendiente';
        updatedSubtasks[index] = { ...current, status: nextState };
        return { ...item, subtasks: updatedSubtasks };
      });

      try {
        await jiraClient.cycleSubtask(ticketKey, index);
        if (!found) await retry('jira');
      } catch {
        await retry('jira');
      }
    },
    [beginWrite, setItems, retry],
  );

  const generateIssueSummary = useCallback(
    async (ticketKey: string) => {
      beginWrite();
      try {
        const res = await jiraClient.generateAiSummary(ticketKey);
        applyIssueMeta(ticketKey, res.data);
      } catch {
        await retry('jira');
      }
    },
    [beginWrite, applyIssueMeta, retry],
  );

  const addJiraComment = useCallback(
    async (ticketKey: string, markdown: string): Promise<void> => {
      beginWrite();
      try {
        await jiraClient.addComment(ticketKey, markdown, createIdempotencyKey('jira-comment'));
      } catch (error) {
        await retry('jira').catch(() => undefined);
        throw error;
      }
      await retry('jira').catch(() => undefined);
    },
    [beginWrite, retry],
  );

  const generateJiraCommentDraft = useCallback(
    async (ticketKey: string, hint?: string): Promise<string | null> => {
      setErrors?.((prev) => ({ ...prev, jira: null }));
      try {
        const res = await jiraClient.generateCommentDraft(ticketKey, hint);
        return res.data.draft?.trim() || null;
      } catch (err) {
        setErrors?.((prev) => ({
          ...prev,
          jira: err instanceof Error ? err.message : 'No se pudo generar el borrador con IA.',
        }));
        return null;
      }
    },
    [setErrors],
  );

  return useMemo(
    () => ({
      rawJiraIssues,
      setRawJiraIssues,
      updateWorkflowState,
      toggleAcceptanceCriterion,
      addAcceptanceCriterion,
      updateAcceptanceCriterion,
      deleteAcceptanceCriterion,
      cycleSubtaskState,
      generateIssueSummary,
      addJiraComment,
      generateJiraCommentDraft,
    }),
    [
      rawJiraIssues,
      updateWorkflowState,
      toggleAcceptanceCriterion,
      addAcceptanceCriterion,
      updateAcceptanceCriterion,
      deleteAcceptanceCriterion,
      cycleSubtaskState,
      generateIssueSummary,
      addJiraComment,
      generateJiraCommentDraft,
    ],
  );
}
